import { action, observable } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import {
    ICustomHubPageDTO,
    IEventHubsDAO,
    IHubPageDetailsDTO,
    IHubPageDTO,
    isCustomPage,
} from '../../../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../../../api/implementations/event-hubs.dao';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../../../../constants/common-labels.constants';
import { CustomPageVM } from './_custom-page/custom-page.viewmodel';
import { PAGE_NAME } from './_custom-page/custom-page.config';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl';
import { NEW_PAGE_PLACEHOLDER_TEXT } from '../../../../event-hubs.text';

export enum ManagePageMode {
    CreatePage = 'CreatePage',
    EditPage = 'EditPage',
}

interface IPageOption {
    label: string;
    value: number;
}

export class ManagePageVM {
    @observable
    private pages: Map<number, IHubPageDTO> = new Map();
    @observable
    private selectedPageId: number | null = null;
    @observable
    private currentPageVM: CustomPageVM = CustomPageVM.createEmpty(this.buildPathChecker());
    @observable
    private loading = false;
    @observable
    private saving = false;

    @observable
    private mode: ManagePageMode = ManagePageMode.CreatePage;

    constructor(
        private readonly versionId: number,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider)
    ) {
        void this.initialize();
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public dispose(): void {
        this.currentPageVM.dispose();
    }

    public getPageOptions(): IPageOption[] {
        return this.getPageList().map(page => ({
            label: page.pageName,
            value: page.hubPageId,
        }));
    }

    public getSelectedPageId(): number | null {
        return this.selectedPageId;
    }

    public getSelectedPageVM(): CustomPageVM {
        return this.currentPageVM;
    }

    public isLoading(): boolean {
        return this.loading;
    }

    public isSaving(): boolean {
        return this.saving;
    }

    public isCreateMode(): boolean {
        return this.mode === ManagePageMode.CreatePage;
    }

    public isCancelDisabled(): boolean {
        return this.isCreateMode() && this.pages.size === 0;
    }

    public isSaveButtonDisabled(): boolean {
        return this.saving;
    }

    public isDropdownDisabled(): boolean {
        return this.isCreateMode() || this.currentPageVM.hasUnsavedChanges();
    }

    @action.bound
    public async initialize(): Promise<void> {
        await this.loadPages();
        if (this.pages.size === 0) {
            this.switchToCreatePageMode();
        } else {
            this.selectPage(this.getFirstPageId());
        }
    }

    private getPageList(): IHubPageDTO[] {
        return Array.from(this.pages.values());
    }

    @action
    private switchToCreatePageMode = (): void => {
        this.currentPageVM.dispose();
        this.mode = ManagePageMode.CreatePage;
        this.currentPageVM = CustomPageVM.createEmpty(this.buildPathChecker());
        void this.currentPageVM.modulesVM.loadEventAttributeOptions();
    };

    @action
    private switchToEditPageMode = (page: ICustomHubPageDTO): void => {
        this.currentPageVM.dispose();
        this.mode = ManagePageMode.EditPage;
        this.currentPageVM = CustomPageVM.createFromPage(page, this.buildPathChecker(page.hubPageId));
        void this.loadPageDetails(page.hubPageId);
    };

    private async loadPageDetails(hubPageId: number): Promise<void> {
        const targetVM = this.currentPageVM;
        try {
            const [pageDetails] = await Promise.all([
                this.eventHubsDAO.getHubPage(hubPageId),
                targetVM.modulesVM.loadEventAttributeOptions(),
            ]);
            if (this.currentPageVM === targetVM) {
                targetVM.modulesVM.setModules(pageDetails.modules);
            }
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        }
    }

    public getSelectedPageNameLabel(): string {
        return this.isCreateMode()
            ? this.getCreateModePageNameLabel()
            : this.getEditModePageNameLabel();
    }

    public isSelectedPageNamePlaceholder(): boolean {
        const typed = this.currentPageVM.getFormVmValuePropsByKey(PAGE_NAME).value as string;
        return this.isCreateMode() && !typed;
    }

    @action
    public selectPage = (id: number): void => {
        if (id === this.selectedPageId && !this.isCreateMode()) {
            return;
        }
        this.selectedPageId = id;
        const page = this.pages.get(id);
        this.switchToEditPageMode(page as ICustomHubPageDTO);
    };

    @action
    public onNewPage = (): void => {
        this.selectedPageId = null;
        this.switchToCreatePageMode();
    };

    @action
    public onCancel = (): void => {
        if (this.isCreateMode()) {
            this.selectPage(this.getFirstPageId());
        } else {
            this.currentPageVM.discardChanges();
        }
    };

    public onSavePage = (): void => {
        void this.savePage();
    };

    private async savePage(): Promise<void> {
        const isPageInvalid = !this.currentPageVM.isVmValid() || !this.currentPageVM.modulesVM.areAllModulesValid();
        if (isPageInvalid) {
            this.currentPageVM.setVmTouched();
            this.currentPageVM.modulesVM.touchAllInvalidModules();
            return;
        }

        this.setSaving(true);

        try {
            const savedPage = this.isCreateMode()
                ? await this.eventHubsDAO.createHubPage(this.versionId, this.currentPageVM.toCreateDTO())
                : await this.eventHubsDAO.updateHubPage(this.currentPageVM.toUpdateDTO());

            this.setPageInMapAndSelect(savedPage);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        } finally {
            this.setSaving(false);
        }
    }

    @action
    private setPageInMapAndSelect(page: IHubPageDetailsDTO): void {
        this.pages.set(page.hubPageId, page);
        this.selectedPageId = page.hubPageId;
        this.mode = ManagePageMode.EditPage;
        if (isCustomPage(page)) {
            const eventAttributeOptions = this.currentPageVM.modulesVM.getEventAttributeOptions();
            const pageVM = CustomPageVM.createFromPage(page, this.buildPathChecker(page.hubPageId));
            pageVM.modulesVM.setEventAttributeOptions(eventAttributeOptions);
            pageVM.modulesVM.setModules(page.modules);
            this.currentPageVM.dispose();
            this.currentPageVM = pageVM;
        }
    }

    private getFirstPageId(): number {
        return this.pages.keys().next().value;
    }

    private buildPathChecker(currentPageId?: number): (path: string) => boolean {
        return (path: string) =>
            this.getPageList().some(p => p.pagePath === path && p.hubPageId !== currentPageId);
    }

    @action
    private setPages(pages: IHubPageDTO[]): void {
        this.pages = new Map(pages.map(page => [page.hubPageId, page]));
    }

    @action
    private setLoading(loading: boolean): void {
        this.loading = loading;
    }

    @action
    private setSaving(saving: boolean): void {
        this.saving = saving;
    }

    private async loadPages(): Promise<void> {
        this.setLoading(true);
        try {
            const pages = await this.eventHubsDAO.getHubPages(this.versionId);
            this.setPages(pages);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), errorMessage);
        } finally {
            this.setLoading(false);
        }
    }

    private getCreateModePageNameLabel(): string {
        const typed = this.currentPageVM.getFormVmValuePropsByKey(PAGE_NAME).value as string;
        const name = typed || this.intl.formatMessage(NEW_PAGE_PLACEHOLDER_TEXT);
        return `${name} *`;
    }

    private getEditModePageNameLabel(): string {
        const typed = this.currentPageVM.getFormVmValuePropsByKey(PAGE_NAME).value as string;
        const name = typed || this.pages.get(this.selectedPageId)?.pageName || '';
        return this.currentPageVM.hasUnsavedChanges() ? `${name} *` : name;
    }
}
